package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.NARROW_NO_BREAK_SPACE
import dev.lingvo.shared.PunctuationKey
import dev.lingvo.shared.nextMissingPlaceholder
import dev.lingvo.shared.punctuationFor
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LPresencePhase
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.rememberLPresence
import dev.lingvo.site.ds.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.EventListener

@Composable
internal fun EditorFieldToolbar(
    state: TranslationEditorState,
    row: KeyRow,
    draft: EditorDraft,
    onInsertText: (String) -> Unit,
) {
    val punctuation = remember(state.locale) { punctuationFor(state.locale) }
    val primaryPunctuation = punctuation.take(4)
    val extraPunctuation = punctuation.drop(4)
    val nextPlaceholder = nextMissingPlaceholder(
        source = activeSourceValue(row, state.pluralCategory),
        target = activeTargetValue(row, draft, state.pluralCategory),
    )
    var extrasOpen by remember(row.key.id, state.locale, state.pluralCategory) {
        mutableStateOf(false)
    }
    val extrasPresence = rememberLPresence(extrasOpen, Motion.FastMs)
    val extrasId = "editor-punctuation-extras-${row.key.id}-${state.locale}"
    val extrasTriggerId = "$extrasId-trigger"

    // **A surface that calls itself a dialog has to behave like one.** It announced
    // `aria-haspopup="dialog"` and `role="dialog"` while owning no key and no focus: Escape fell
    // through to the editor's global handler and closed the *editor* instead, a pointer anywhere
    // else left it hanging open over the text, and opening it moved focus nowhere, so a keyboard
    // user was told a dialog had opened and then left standing outside it.
    var extrasEverOpened by remember(row.key.id, state.locale) { mutableStateOf(false) }
    LaunchedEffect(extrasPresence.rendered, extrasPresence.phase, extrasId) {
        if (extrasPresence.rendered && extrasPresence.phase != LPresencePhase.EXITING) {
            extrasEverOpened = true
            (document.getElementById(extrasId)?.querySelector("button") as? HTMLElement)?.focus()
        } else if (!extrasPresence.rendered && extrasEverOpened) {
            // **Give focus back only if nobody else has claimed it** — the rule `LModal` states and
            // implements, copied here because this popover copied everything else from it except
            // this. The restore was unconditional and fires 120ms later, when the exit completes,
            // so it landed *after* `onInsertText` had put the caret back in the field: picking a
            // character pulled focus onto the More button, and the translator's next keystroke went
            // nowhere. Worse than nothing typed — with focus on a button, ArrowDown jumps to
            // another key and Space re-opens the popover. Clicking from the open popover straight
            // into the text has the same shape, which is why this checks where focus *is* rather
            // than how the popover closed.
            val active = document.activeElement
            val claimedElsewhere = active != null &&
                active != document.body &&
                document.getElementById(extrasId)?.contains(active) != true
            if (!claimedElsewhere) {
                (document.getElementById(extrasTriggerId) as? HTMLElement)?.focus()
            }
        }
    }
    DisposableEffect(extrasOpen, extrasId) {
        if (!extrasOpen) return@DisposableEffect onDispose { }
        val dismiss = EventListener { event ->
            val target = event.target as? Node ?: return@EventListener
            val popover = document.getElementById(extrasId)
            val trigger = document.getElementById(extrasTriggerId)
            if (popover?.contains(target) != true && trigger?.contains(target) != true) {
                extrasOpen = false
            }
        }
        document.addEventListener("pointerdown", dismiss, true)
        onDispose { document.removeEventListener("pointerdown", dismiss, true) }
    }

    Row(
        EditorFieldToolbarStyle.toModifier()
            .fillMaxWidth()
            .attr("role", "toolbar")
            .attr("aria-label", t("editor.editor_field_toolbar.translation_tools", "Translation tools"))
            .attr("data-editor-field-toolbar", "true"),
    ) {
        primaryPunctuation.forEach { key ->
            InlinePunctuationKey(key) { onInsertText(key.character) }
        }
        if (extraPunctuation.isNotEmpty()) {
            Div(EditorFieldToolbarMoreStyle.toModifier().toAttrs()) {
                LButton(
                    label = t("editor.editor_field_toolbar.more", "More"),
                    onClick = { extrasOpen = !extrasOpen },
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    // Translated, and leading with the visible word. The literal announced English
                    // in every locale — so a reader whose interface is German heard "More
                    // punctuation" beside a button reading "Mehr", and the accessible name stopped
                    // containing the visible label, which is the speech-input rule (WCAG 2.5.3).
                    accessibleLabel = t(
                        "editor.editor_field_toolbar.more_punctuation",
                        "More punctuation",
                    ),
                    modifier = Modifier
                        .attr("id", extrasTriggerId)
                        .attr("aria-haspopup", "dialog")
                        .attr("aria-controls", extrasId)
                        .attr("aria-expanded", extrasOpen.toString())
                        .then(EditorCoarseTargetStyle.toModifier())
                        .attr("data-editor-coarse-target", "tool"),
                )
                if (extrasPresence.rendered) {
                    val exiting = extrasPresence.phase == LPresencePhase.EXITING
                    Div(
                        EditorFieldToolbarPopoverStyle.toModifier()
                            .attr("data-presence", extrasPresence.phase.attributeValue)
                            .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier)
                            .toAttrs {
                            id(extrasId)
                            attr("role", "dialog")
                            attr("aria-modal", "false")
                            attr("aria-label", tr("editor.editor_field_toolbar.more_punctuation", "More punctuation"))
                            onKeyDown { event ->
                                if (event.key == "Escape") {
                                    // Stopped here so the editor's own Escape does not also fire and
                                    // close the surface behind this one. Topmost owner wins.
                                    event.preventDefault()
                                    event.stopPropagation()
                                    extrasOpen = false
                                }
                            }
                        },
                    ) {
                        extraPunctuation.forEach { key ->
                            PopoverPunctuationKey(key) {
                                onInsertText(key.character)
                                extrasOpen = false
                            }
                        }
                    }
                }
            }
        }
        nextPlaceholder?.let { placeholder ->
            LButton(
                label = placeholder,
                onClick = { onInsertText(placeholder) },
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                modifier = Modifier
                    .attr(
                        "title",
                        tr(
                            "editor.editor_field_toolbar.insert_the_next_placeholder_from_the",
                            "Insert the next placeholder from the source (Alt+P)",
                        ),
                    )
                    .then(EditorCoarseTargetStyle.toModifier())
                    .attr("data-editor-coarse-target", "tool"),
                accessibleLabel = tf(
                    "editor.editor_field_toolbar.insert_placeholder_from_the_source",
                    "Insert {placeholder} from the source (Alt+P)",
                    "placeholder" to placeholder,
                ),
            )
        }
    }
}

/**
 * A key in the strip, with its hint as a real text node beside it.
 *
 * The hint used to travel only as `title` plus `aria-description`, under a comment claiming the
 * More popover rendered it as visible text — which it did not: the popover reused this same button,
 * whose only content is the character. So the sentence that says *when* to type U+202F was
 * unreachable on the touch device the 44px targets exist for, and `aria-description` is thinly
 * enough implemented to be no guarantee either.
 *
 * `EditorFieldToolbarStyle` clips the hint at a fine pointer, where the tooltip works, and lays it
 * out as visible text at a coarse one. It is clipped rather than hidden, so it is read aloud in
 * both cases and there is exactly one carrier to keep true.
 */
@Composable
private fun InlinePunctuationKey(
    key: PunctuationKey,
    onInsert: () -> Unit,
) {
    Div(Modifier.attr("data-editor-punctuation-item", "true").toAttrs()) {
        PunctuationButton(key, onInsert)
        Span(Modifier.attr("data-editor-punctuation-hint", "true").toAttrs()) {
            Text(key.hint)
        }
    }
}

/**
 * A key in the **More** popover, named and explained in visible text.
 *
 * A popover is opened deliberately and has room, so the character alone — which is what this used
 * to render — tells a reader nothing about a mark they went looking for and could not recognise.
 */
@Composable
private fun PopoverPunctuationKey(
    key: PunctuationKey,
    onInsert: () -> Unit,
) {
    Row(
        Modifier.fillMaxWidth().gap(Space.SM),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        PunctuationButton(key, onInsert)
        Column(Modifier.gap(Space.XXS)) {
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textPrimary)
                    .fontSize(TypeScale.Small)
                    .fontWeight(600)
                    .toAttrs(),
            ) {
                Text(key.label)
            }
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textMuted)
                    .fontSize(TypeScale.Small)
                    .lineHeight(1.4)
                    .toAttrs(),
            ) {
                Text(key.hint)
            }
        }
    }
}

@Composable
private fun PunctuationButton(
    key: PunctuationKey,
    onClick: () -> Unit,
) {
    LButton(
        label = key.visualLabel(),
        onClick = onClick,
        // **SECONDARY, because a bare mark is not a control.** As GHOST these four were four
        // unattached glyphs under the target field; the hairline and surface are what make them
        // read as keys. See `EditorFieldToolbarStyle`'s `[data-editor-punctuation-key]` rule for
        // the square that puts them all on one optical footing.
        variant = LButtonVariant.SECONDARY,
        size = LButtonSize.SM,
        modifier = Modifier
            .attr("data-editor-punctuation-key", "true")
            // The pointer affordance, and only that. The sentence itself is a text node next to the
            // button — see InlinePunctuationKey — because a tooltip cannot be opened by keyboard or
            // by touch.
            .attr(
                "title",
                trf(
                    "editor.editor_field_toolbar.label_hint",
                    "{label} - {hint}",
                    "label" to key.label,
                    "hint" to key.hint,
                ),
            )
            .then(EditorCoarseTargetStyle.toModifier())
            .attr("data-editor-coarse-target", "tool"),
        accessibleLabel = key.label,
    )
}

private fun PunctuationKey.visualLabel(): String =
    if (character == NARROW_NO_BREAK_SPACE) "␣" else character
